import { useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  checkPasswordCriteria,
  validateSetupProfile,
  validateUsername,
} from '@wlb/shared/utils/validation';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { PasswordCriteria } from '../features/auth/PasswordCriteria';
import { useUsernameAvailability } from '../features/auth/useUsernameAvailability';
import '../features/auth/auth.css';

interface SignupLocationState {
  email?: string;
}

export function SignUpProfile() {
  const { t } = useTranslation();
  const { signUpWithEmail, completeAuthSession, token, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as SignupLocationState | null)?.email ?? '';
  const [form, setForm] = useState({ username: '', password: '', referralCode: '' });
  const [dirty, setDirty] = useState(false);
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const errors = useMemo(() => {
    if (!dirty) return {};
    return validateSetupProfile(form);
  }, [dirty, form]);

  const passwordValid = Object.values(checkPasswordCriteria(form.password)).every(Boolean);

  // Username format rules (min 8, letters and numbers only) come from the shared
  // validator so web and mobile reject the same values.
  const usernameCheck = validateUsername(form.username);
  // Shown while typing once the field has been used, so the disabled Continue
  // button always has a visible reason next to the input.
  const usernameError =
    (usernameTouched || dirty) && !usernameCheck.valid ? t(usernameCheck.message) : '';

  // Username live availability. The hook is dormant until the API can answer
  // the check, so today only the format rule above governs the field.
  const { status: usernameAvailability } = useUsernameAvailability(form.username);
  const usernameChecking = usernameAvailability === 'checking';
  const usernameTaken = usernameAvailability === 'taken';
  const usernameBlocked = !usernameCheck.valid || usernameChecking || usernameTaken;

  const update = (key: keyof typeof form, value: string) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  /**
   * "Maybe later" ends onboarding early: the account already exists, so promote
   * the stored token to a real session and drop the user into the feed. Navigate
   * first — activating the session remounts the shell around the new route.
   */
  const enterAppWithoutOnboarding = async (): Promise<void> => {
    navigate('/', { replace: true });
    await completeAuthSession(token ?? undefined);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setDirty(true);
    // Validate synchronously — the memoized `errors` is stale on the first
    // submit because setDirty(true) hasn't re-rendered yet.
    const validationErrors = validateSetupProfile(form);
    if (!email || Object.keys(validationErrors).length > 0 || !passwordValid) return;

    try {
      await signUpWithEmail({
        email,
        // The legacy API still accepts this field; mirror the username until
        // the backend contract removes personal-name fields.
        firstName: form.username.trim(),
        username: form.username.trim(),
        password: form.password,
        ...(form.referralCode.trim() ? { referralCode: form.referralCode.trim() } : {}),
      });
      setShowSuccess(true);
    } catch {
      // AuthContext exposes the translated API error.
    }
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <button className="auth-flow__back" type="button" onClick={() => navigate('/signup')}>
          ‹ {t('common.buttons.back')}
        </button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.setupProfile.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.setupProfile.subheading')}</p>

        <form className="auth-flow__form" onSubmit={submit}>
          <label className="auth-flow__field">
            <span>{t('auth.setupProfile.usernameLabel')}</span>
            <input
              className={`auth-flow__input${usernameError || usernameTaken ? ' auth-flow__input--error' : ''}`}
              type="text"
              value={form.username}
              onChange={event => {
                setUsernameTouched(true);
                update('username', event.target.value);
              }}
              onBlur={() => setUsernameTouched(true)}
              placeholder={t('auth.setupProfile.usernamePlaceholder')}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={Boolean(usernameError) || usernameTaken}
              aria-describedby={`signup-profile-username-helper${usernameError ? ' signup-profile-username-error' : ''}${usernameChecking || usernameAvailability === 'available' || usernameTaken ? ' signup-profile-username-status' : ''}`}
            />
            <p id="signup-profile-username-helper" className="auth-flow__helper-text">
              {t('auth.setupProfile.usernameRequirement')}
            </p>
            {(usernameChecking || usernameAvailability === 'available' || usernameTaken) && (
              <p
                id="signup-profile-username-status"
                className={`username-status username-status--${usernameAvailability}`}
                role={usernameTaken ? 'alert' : 'status'}
              >
                <span className="username-status__mark" aria-hidden>
                  {usernameChecking ? '•' : usernameAvailability === 'available' ? '✓' : '✕'}
                </span>
                {usernameChecking && t('auth.setupProfile.usernameChecking')}
                {usernameAvailability === 'available' && t('auth.setupProfile.usernameAvailable')}
                {usernameTaken && t('auth.setupProfile.usernameTaken')}
              </p>
            )}
            <FormError message={usernameError} id="signup-profile-username-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('auth.setupProfile.passwordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${errors.password ? ' auth-flow__input--error' : ''}`}
              value={form.password}
              onChange={event => update('password', event.target.value)}
              placeholder={t('auth.setupProfile.passwordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={`signup-profile-password-criteria${errors.password ? ' signup-profile-password-error' : ''}`}
            />
            <PasswordCriteria password={form.password} id="signup-profile-password-criteria" />
            <FormError
              message={errors.password ? t(errors.password) : ''}
              id="signup-profile-password-error"
            />
          </label>

          <label className="auth-flow__field">
            <span>{t('auth.setupProfile.referralLabel')}</span>
            <input
              className={`auth-flow__input${errors.referralCode ? ' auth-flow__input--error' : ''}`}
              type="text"
              value={form.referralCode}
              onChange={event => update('referralCode', event.target.value)}
              placeholder={t('auth.setupProfile.referralPlaceholder')}
              autoComplete="off"
              aria-invalid={Boolean(errors.referralCode)}
              aria-describedby={errors.referralCode ? 'signup-profile-referralCode-error' : undefined}
            />
            <FormError
              message={errors.referralCode ? t(errors.referralCode) : ''}
              id="signup-profile-referralCode-error"
            />
          </label>
          <FormError
            variant="banner"
            message={error || (!email && dirty ? t('auth.errors.emailRequired') : '')}
          />
          <button className="auth-flow__primary" type="submit" disabled={loading || usernameBlocked}>
            {loading ? t('common.loading') : t('common.buttons.createAccount')}
          </button>
        </form>

        <p className="auth-flow__footer">
          {t('auth.setupProfile.alreadyHaveAccount')}
          <Link to="/login">{t('common.buttons.logIn')}</Link>
        </p>
      </div>

      {showSuccess && (
        <div className="auth-flow__modal-overlay" role="presentation">
          <div className="auth-flow__modal" role="dialog" aria-modal="true" aria-labelledby="account-created-title">
            <BrandLogo compact className="auth-flow__modal-mark" />
            <h2 id="account-created-title">{t('modals.accountCreated.title')}</h2>
            <p>{t('modals.accountCreated.subtitle')}</p>
            <div className="auth-flow__modal-actions">
              <button className="auth-flow__social" type="button" onClick={enterAppWithoutOnboarding}>
                {t('common.buttons.maybeLater')}
              </button>
              <button className="auth-flow__primary" type="button" onClick={() => navigate('/basic-info')}>
                {t('common.buttons.continue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthFlowLayout>
  );
}
